import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "~/css/ChatHistoryGraph.css";
import ConversationAccordion from "./utils/ConversationAccordion";
import { useTranslation } from "react-i18next";
import { useChatHistoryGraph } from "./hooks/useChatHistoryGraph";
import type { ConversationMessage } from "shared/Schemas";
import { removeDollarKeys } from "~/utils/chatMessageUtils";
import { feedbackModes } from "~/Commons";
import {
  calcAverageStar,
  calcSimpleFeedbackSummary,
  AverageStarDisplay,
} from "./utils/chatHistoryGraphUtils";

const ChatHistoryGraph: React.FC = () => {
  const { t } = useTranslation();
  const {
    dailyData,
    conversationsData,
    loading,
    error,
    feedbackData,
    feedbackDetailData,
    feedbackMode,
    startDate,
    endDate,
    filteredData,
    customImagesLoaded,
    legendItems,
    legendDetailItems,
    setStartDate,
    setEndDate,
    fetchData,
    resetDateRange,
    downloadCSV,
    convertLabel,
    convertStarLabel,
    formatDate,
    formatDateTimeYColon,
  } = useChatHistoryGraph();

  // グラフの色設定
  const GOOD_COLOR = "#5CDD9D"; // GOOD
  const BAD_COLOR = "#EA8484"; // BAD
  // 星1〜5のグラデーション色
  const STAR_COLORS: Record<number, string> = {
    1: "#C93535",
    2: "#E88080",
    3: "#80CCA0",
    4: "#3DB87A",
    5: "#1A9E5E",
  };

  /**
   * HTMLタグを除去する
   * @param html HTML文字列
   * @returns プレーンテキスト
   */
  const stripHtmlTags = (html: string): string => {
    try {
      if (typeof window === "undefined" || !("DOMParser" in window)) {
        return html.replace(/<[^>]*>/g, "");
      }
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      return doc.body.textContent || "";
    } catch {
      // パースに失敗した場合は従来の正規表現による除去にフォールバック
      return html.replace(/<[^>]*>/g, "");
    }
  };

  /**
   * 単純な許可タグ(img, br)のみサポートして段落化する(HTML→JSX変換)
   *
   * @param html 変換するHTML文字列
   * @returns
   */
  const renderHtmlWithParagraphs = (html: string): React.ReactNode => {
    // \nをすべて削除する
    let content = html.replace(/\n/g, "");

    // $$text$$を別のキューに退避する（テキストからは完全に除去）
    const dollarTextElements: React.ReactNode[] = [];
    const imgElements: React.ReactNode[] = [];
    let keyCounter = 0;

    // $$text$$を抽出してキューに退避
    content.replace(/\$\$([^$]+)\$\$/g, (match, key) => {
      const customImage = customImagesLoaded.find((ci) => ci.key === key.trim());
      if (!customImage) return match;

      dollarTextElements.push(
        <div key={`${customImage.key}-${keyCounter++}`} className="custom-image">
          <img src={customImage.src} alt={customImage.key} />
        </div>
      );
      return match;
    });

    // テキストから $$key$$ を完全に削除
    content = removeDollarKeys(content);

    const isSafeSrc = (src: string) =>
      /^https?:\/\//i.test(src) || /^data:image\/(jpeg|jpg|png|gif|webp);base64,/i.test(src);

    const createImgElement = (tag: string): React.ReactNode | null => {
      try {
        const parser = new window.DOMParser();
        const doc = parser.parseFromString(tag, "text/html");
        const img = doc.querySelector("img");
        if (!img) return null;
        const src = img.getAttribute("src") || "";
        if (!isSafeSrc(src)) return null;
        const alt = img.getAttribute("alt") || "";
        const widthAttr = img.getAttribute("width");
        const heightAttr = img.getAttribute("height");
        const width = widthAttr && /^\d+$/.test(widthAttr) ? Number(widthAttr) : undefined;
        const height = heightAttr && /^\d+$/.test(heightAttr) ? Number(heightAttr) : undefined;
        return (
          <img key={`img-${keyCounter++}`} src={src} alt={alt} width={width} height={height} />
        );
      } catch {
        return null;
      }
    };

    // imgタグを抽出してキューに退避（テキストからは完全に除去）
    content = content.replace(/<img[^>]*>/gi, (match) => {
      const imgElement = createImgElement(match);
      if (imgElement) {
        imgElements.push(imgElement);
      }
      return ""; // テキストからは完全に除去
    });

    // その他のHTMLタグを削除
    content = stripHtmlTags(content);

    // 「！？。.!?」の一文ごとに<p>タグで囲む
    const sentences = content.split(/([！？。.!?])/);
    const paragraphs: React.ReactNode[] = [];

    // 句読点とテキストをペアで処理
    for (let i = 0; i < sentences.length; i += 2) {
      const sentence = sentences[i];
      const punctuation = sentences[i + 1] || "";

      if (sentence.trim() || punctuation) {
        const fullSentence = sentence + punctuation;

        // 文にコンテンツがある場合のみ<p>タグを作成
        if (fullSentence.trim()) {
          paragraphs.push(<p key={`p-${keyCounter++}`}>{fullSentence}</p>);
        }
      }
    }

    // 最後に段落 → $$テキスト → imgタグの順で出力
    const allElements = [...paragraphs, ...dollarTextElements, ...imgElements];

    return <div className="rendered-html">{allElements}</div>;
  };

  if (loading) {
    return (
      <div className="chat-history-loading-container">
        <div className="chat-history-loading-spinner"></div>
        <p className="chat-history-loading-text">{t("loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-text">
          {t("error")}: {error}
        </p>
        <button className="retry-button" onClick={fetchData}>
          {t("retry")}
        </button>
      </div>
    );
  }

  return (
    <div className="chat-history-graph-container">
      <div className="chat-history-graph-content">
        {/* 日付範囲選択コントロール */}
        <div className="date-range-controls">
          <span className="date-range-label">{t("displayPeriod")}</span>
          <div className="date-picker-group">
            <div className="date-picker-wrapper">
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => date && setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                maxDate={endDate}
                dateFormat="yyyy.MM.dd"
                className="date-picker-input"
                placeholderText={t("startDatePlaceholder")}
              />
              <span className="material-symbols-rounded date-picker-icon">calendar_month</span>
            </div>
            <span className="date-range-from-label">～</span>
            <div className="date-picker-wrapper">
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => date && setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                maxDate={new Date()}
                dateFormat="yyyy.MM.dd"
                className="date-picker-input"
                placeholderText={t("endDatePlaceholder")}
              />
              <span className="material-symbols-rounded date-picker-icon">calendar_month</span>
            </div>
            <button className="button-1 custom-button" onClick={resetDateRange}>
              {t("last30Days")}
            </button>
          </div>
          <span className="total-count">
            {t("conversationsDuringThisPeriod")}： {filteredData.length}
          </span>
        </div>

        {/* 日別会話数の推移グラフ */}
        <div className="section">
          <h3 className="section-title">
            {t("dailyConversationNumbers")} ({formatDate(startDate)} ～ {formatDate(endDate)})
          </h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{
                    fontSize: 16,
                    fontWeight: 400,
                    fontFamily: "Noto Sans JP",
                  }}
                  angle={-63}
                  textAnchor="end"
                  height={100}
                  tickFormatter={(value: string, index: number) => {
                    if (!value) return "";
                    // 期待フォーマット: YYYY-MM-DD
                    const parts = value.split("-");
                    if (parts.length < 3) return value; // 想定外フォーマットはそのまま
                    const [y, m, d] = parts;
                    return index === 0 ? `${y}.${m}.${d}` : `${m}.${d}`;
                  }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{
                    fontSize: 18,
                    fontWeight: 400,
                    fontFamily: "Hiragino Kaku Gothic ProN",
                  }}
                  tickMargin={15}
                  width={70}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value: string) => value}
                />
                <Tooltip
                  labelFormatter={(value) => `${t("date")}: ${value}`}
                  formatter={(value) => [`${value}${t("number")}`, t("numberOfConversations")]}
                />
                <Line
                  type="linear"
                  dataKey="count"
                  stroke="#248CB7"
                  strokeWidth={2}
                  dot={{ fill: "#248CB7", strokeWidth: 3, r: 4 }}
                  name={t("numberOfConversations")}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* フィードバックグラフ */}
        {/* 5段階モード: 星1~5の内訳にデータがあれば表示 */}
        {feedbackMode === feedbackModes.detail && feedbackDetailData.some((d) => d.count > 0) && (
          <div className="section">
            <h3 className="section-title">{t("numberAndPercentageOfRatingsByFeedback")}</h3>
            <div className="chart-container">
              <div className="chart-content">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={feedbackDetailData} margin={{ top: 40 }}>
                    <CartesianGrid strokeDasharray="2 3" vertical={false} />
                    <XAxis
                      dataKey="star"
                      tick={{
                        fontSize: 20,
                        fontFamily: "Hiragino Kaku Gothic ProN",
                      }}
                      tickLine={false}
                      axisLine={false}
                      tickMargin={20}
                      tickFormatter={(value: number, index: number) => {
                        return `${feedbackDetailData[index]?.count || 0}${t("number")}`;
                      }}
                    />
                    <YAxis
                      tick={{
                        fontSize: 18,
                        fontWeight: 400,
                        fontFamily: "Hiragino Kaku Gothic ProN",
                      }}
                      tickMargin={20}
                      width={70}
                      tickLine={false}
                      tickFormatter={(value: string) => value}
                      axisLine={false}
                    />
                    <Tooltip
                      cursor={false}
                      labelFormatter={(value) => convertStarLabel(Number(value))}
                      formatter={(value) => [`${value}${t("number")}`, `${t("numberOfRatings")}`]}
                    />
                    <Legend
                      align="right"
                      wrapperStyle={{
                        top: 0,
                        right: 0,
                      }}
                      content={() => (
                        <ul className="legend-row-container">
                          {legendDetailItems.map((item) => (
                            <li key={item.key}>
                              <div
                                className="circle-icon circle-icon--star"
                                style={{
                                  backgroundColor: STAR_COLORS[item.key as number] ?? GOOD_COLOR,
                                }}
                              ></div>
                              <span className="icon-label">{item.label}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    />
                    <Bar dataKey="count" name={`${t("numberOfRatings")}`} barSize={40}>
                      {feedbackDetailData.map((entry) => (
                        <Cell key={entry.star} fill={STAR_COLORS[entry.star] ?? GOOD_COLOR} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-content">
                <div className="pie-row">
                  <div className="pie-canvas">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={feedbackDetailData}
                          labelLine={false}
                          stroke="none"
                          nameKey="star"
                          startAngle={450}
                          endAngle={90}
                          dataKey="count"
                        >
                          {feedbackDetailData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={STAR_COLORS[entry.star] ?? GOOD_COLOR}
                              stroke="none"
                              strokeWidth={0}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          cursor={false}
                          formatter={(value, name, ctx) => {
                            const s = ctx.payload?.star;
                            return [
                              `${value}${t("number")}`,
                              s !== undefined ? convertStarLabel(s) : name,
                            ];
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="pie-legend">
                    {legendDetailItems.map((item) => (
                      <li className="pie-legend-item" key={item.key}>
                        <div
                          className="circle-icon circle-icon--star"
                          style={{ backgroundColor: STAR_COLORS[item.key as number] ?? GOOD_COLOR }}
                        ></div>
                        <span className="icon-label">{item.label}</span>
                        <span className="icon-percent-label">{item.percent}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* シンプルモード: GOOD/BAD別フィードバックグラフ */}
        {feedbackMode === feedbackModes.simple && feedbackData.length > 0 && (
          <div className="section">
            <h3 className="section-title">{t("numberAndPercentageOfRatingsByFeedback")}</h3>
            <div className="chart-container">
              <div className="chart-content">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={feedbackData} margin={{ top: 40 }}>
                    <CartesianGrid strokeDasharray="2 3" vertical={false} />
                    <XAxis
                      dataKey="rating"
                      tick={{
                        fontSize: 20,
                        fontFamily: "Hiragino Kaku Gothic ProN",
                      }}
                      tickLine={false}
                      axisLine={false}
                      tickMargin={20}
                      tickFormatter={(value: string, index: number) => {
                        return `${feedbackData[index]?.count || 0}${t("number")}`;
                      }}
                    />
                    <YAxis
                      tick={{
                        fontSize: 18,
                        fontWeight: 400,
                        fontFamily: "Hiragino Kaku Gothic ProN",
                      }}
                      tickMargin={20}
                      width={70}
                      tickLine={false}
                      tickFormatter={(value: string) => value}
                      axisLine={false}
                    />
                    <Tooltip
                      cursor={false}
                      labelFormatter={(value) => convertLabel(value)}
                      formatter={(value) => [`${value}${t("number")}`, `${t("numberOfRatings")}`]}
                    />
                    <Legend
                      align="right"
                      wrapperStyle={{
                        top: 0,
                        right: 0,
                      }}
                      content={() => (
                        <ul className="legend-row-container">
                          {legendItems.map((item) => (
                            <li key={item.key}>
                              {item.key === "like" ? (
                                <div className="circle-icon good">
                                  <span className="material-symbols-rounded">thumb_up</span>
                                </div>
                              ) : (
                                <div className="circle-icon bad">
                                  <span className="material-symbols-rounded">thumb_down</span>
                                </div>
                              )}
                              <span className="icon-label">{item.label}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    />
                    <Bar dataKey="count" name={`${t("numberOfRatings")}`} barSize={60}>
                      {feedbackData.map((entry) => (
                        <Cell
                          key={entry.rating}
                          fill={entry.rating === "like" ? GOOD_COLOR : BAD_COLOR}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-content">
                <div className="pie-row">
                  <div className="pie-canvas">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={feedbackData}
                          labelLine={false}
                          stroke="none"
                          nameKey="rating"
                          startAngle={450}
                          endAngle={90}
                          dataKey="count"
                        >
                          {feedbackData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.rating === "like" ? GOOD_COLOR : BAD_COLOR}
                              stroke="none"
                              strokeWidth={0}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          cursor={false}
                          formatter={(value, name, ctx: { payload?: { rating?: string } }) => {
                            const rating = ctx.payload?.rating ?? name;
                            return [`${value ?? 0}${t("number")}`, convertLabel(String(rating))];
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="pie-legend">
                    {legendItems.map((item) => (
                      <li className="pie-legend-item" key={item.key}>
                        {item.key === "like" ? (
                          <div className="circle-icon good">
                            <span className="material-symbols-rounded">thumb_up</span>
                          </div>
                        ) : (
                          <div className="circle-icon bad">
                            <span className="material-symbols-rounded">thumb_down</span>
                          </div>
                        )}
                        <span className="icon-label">{item.label}</span>
                        <span className="icon-percent-label">{item.percent}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 会話一覧セクション */}
        <div className="section">
          <div className="conversation-list-section">
            <h3 className="section-title">{t("conversationList")}</h3>
            <div className="conversation-actions">
              <button className="button-0 custom-button" onClick={fetchData}>
                {t("UpdateData")}
              </button>
              <button className="button-1 custom-button" onClick={downloadCSV}>
                {t("csvOutput")}
              </button>
            </div>
          </div>
          <div className="conversation-accordion">
            <ConversationAccordion
              items={(filteredData.length > 0 ? filteredData : conversationsData).map((conv) => ({
                title: (
                  <div className="conversation-title">
                    <span className="conversation-name">{conv.name}</span>
                    <span className="conversation-date">
                      {formatDateTimeYColon(new Date(conv.created_at * 1000))}
                    </span>
                    <div className="detail-row">
                      {(() => {
                        const avg = calcAverageStar(conv.messages);
                        if (avg !== null) {
                          // 星評価データあり → 平均星表示
                          return <AverageStarDisplay average={avg} />;
                        }
                        // 星評価なし → GOOD/BAD集約
                        const summary = calcSimpleFeedbackSummary(conv.messages);
                        if (summary === "like") {
                          return (
                            <div className="ellipse-icon good">
                              <span className="material-symbols-rounded">thumb_up</span>
                              <span>{convertLabel("like")}</span>
                            </div>
                          );
                        }
                        if (summary === "dislike") {
                          return (
                            <div className="ellipse-icon bad">
                              <span className="material-symbols-rounded">thumb_down</span>
                              <span>{convertLabel("dislike")}</span>
                            </div>
                          );
                        }
                        if (summary === "mixed") {
                          return (
                            <div className="ellipse-icon mixed">
                              <span className="material-symbols-rounded">thumbs_up_down</span>
                              <span>MIXED</span>
                            </div>
                          );
                        }
                        return (
                          <div className="ellipse-icon unrated">
                            <span>{t("unrated")}</span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ),
                content: (
                  <div>
                    {conv.messages && conv.messages.length > 0 ? (
                      <>
                        {/* eslint-disable-next-line */}
                        {conv.messages.map((message: ConversationMessage, index: number) => {
                          const isUnrated = !message.feedback?.rating;
                          const isStar =
                            message.feedback?.content && /^[1-5]$/.test(message.feedback.content);
                          const isLike = message.feedback?.rating === "like";
                          return (
                            <div key={index} className="message-item">
                              <div className="message-query">
                                <span className="message-query-icon">{t("you")}</span>
                                {(() => {
                                  try {
                                    const parsed = JSON.parse(message.query.replace(/'/g, '"'));
                                    return parsed.message || message.query;
                                  } catch {
                                    return message.query;
                                  }
                                })()}
                              </div>
                              <div className="message-answer">
                                <div className="markdown-content">
                                  {(() => {
                                    return renderHtmlWithParagraphs(message.answer);
                                  })()}
                                </div>
                              </div>
                              {/* メッセージ単位のフィードバック表示（未評価含む常時表示） */}
                              <div className="message-feedback">
                                {isUnrated ? (
                                  <div className="ellipse-icon unrated">
                                    <span>{t("unrated")}</span>
                                  </div>
                                ) : isStar ? (
                                  <div
                                    className="star-rating-label"
                                    style={{
                                      color: STAR_COLORS[Number(message.feedback?.content)],
                                    }}
                                  >
                                    {Array.from(
                                      { length: Number(message.feedback?.content) },
                                      (_, i) => (
                                        <span
                                          key={i}
                                          className="material-symbols-rounded star-rating-icon"
                                        >
                                          star
                                        </span>
                                      )
                                    )}
                                  </div>
                                ) : isLike ? (
                                  <div className="ellipse-icon good">
                                    <span className="material-symbols-rounded">thumb_up</span>
                                    <span>{convertLabel(message.feedback?.rating ?? "")}</span>
                                  </div>
                                ) : (
                                  <div className="ellipse-icon bad">
                                    <span className="material-symbols-rounded">thumb_down</span>
                                    <span>{convertLabel(message.feedback?.rating ?? "")}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <>{t("noMessage")}</>
                    )}
                  </div>
                ),
                externalContent: <div className="external-content">ID : {conv.id}</div>,
              }))}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatHistoryGraph;
